// frontend-react/src/services/apiService.js
// Complete rewrite with MLB data endpoints and improved organization
// UPDATED: Tile analytics endpoints now accept optional leagueId parameter

import axios from 'axios';
import logger from '../utils/logger';
import { validateCustomWindow } from '../utils/customWindowValidator';
import { formatApiError } from './errorUtils';

// Helper to store/retrieve access token for mobile (fallback when cookies don't work)
const TOKEN_STORAGE_KEY = 'fantasy_auth_token';

const getStoredToken = () => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const setStoredToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch (e) {
    console.warn('Failed to store token:', e);
  }
};

// Create axios instance with base configuration
const api = axios.create({
  withCredentials: true,
  baseURL: process.env.REACT_APP_API_URL || (() => {
    if (process.env.NODE_ENV === 'production') {
      throw new Error('REACT_APP_API_URL environment variable is required in production');
    }
    return 'https://13cpdms4x1.execute-api.us-east-1.amazonaws.com/Prod';
  })(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor for debugging and token injection
api.interceptors.request.use(
  (config) => {
    // Add Authorization header if we have a stored token (for mobile fallback when cookies don't work)
    const storedToken = getStoredToken();
    if (storedToken && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${storedToken}`;
    }
    
    const logData = {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      withCredentials: config.withCredentials,
      headers: { 
        ...config.headers, 
        Authorization: config.headers['Authorization'] ? '***PRESENT***' : 'Missing' 
      },
      hasData: !!config.data,
      dataType: config.data ? typeof config.data : null,
      timestamp: new Date().toISOString()
    };
    
    // Special logging for auth endpoints
    if (config.url?.includes('/auth/')) {
      console.log('🔐 API Request Interceptor (AUTH):', logData);
      if (config.data) {
        const sanitizedData = { ...config.data };
        if (sanitizedData.password) sanitizedData.password = '***REDACTED***';
        console.log('🔐 API Request Data (AUTH):', sanitizedData);
      }
    } else {
      logger.debug(`📡 API: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ API Request Interceptor Error:', {
      errorType: error.constructor.name,
      message: error.message,
      stack: error.stack
    });
    logger.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Silent token refresh — intercepts 401s, calls /auth/refresh with retry, retries original request
let isRefreshing = false;
let failedQueue = [];
let authDefinitelyFailed = false;

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) { prom.reject(error); } else { prom.resolve(); }
  });
  failedQueue = [];
};

// Background poll detection — these requests should not trigger logout on auth failure
const isBackgroundPoll = (config) => {
  const url = config?.url || '';
  return url.includes('/live-scoring/') ||
    url.includes('/notifications/count') ||
    url.includes('/minor-league-draft/notifications/count') ||
    url.includes('/messages/unread/count');
};

// Retry refresh up to 3 times with 2s delay on transient (503) failures
const attemptRefreshWithRetry = async () => {
  const MAX_ATTEMPTS = 3;
  const RETRY_DELAY = 2000;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      await api.post('/api/auth/refresh');
      authDefinitelyFailed = false;
      return;
    } catch (refreshError) {
      if (refreshError.response?.status === 401) {
        // Definitive failure — refresh token invalid/expired
        authDefinitelyFailed = true;
        throw refreshError;
      }
      // Transient failure (503) — retry after delay
      if (attempt < MAX_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      } else {
        throw refreshError;
      }
    }
  }
};

api.interceptors.response.use(
  (response) => {
    // Reset auth failure flag on successful login (new session has valid refresh token)
    if (response.config?.url?.includes('/auth/login') && response.status === 200) {
      authDefinitelyFailed = false;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      originalRequest._retry = true;

      // If auth already definitively failed, skip refresh for background polls
      if (isBackgroundPoll(originalRequest) && authDefinitelyFailed) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest)).catch(err => Promise.reject(err));
      }
      isRefreshing = true;
      try {
        await attemptRefreshWithRetry();
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Background polls silently skip on permanent auth failure
        if (isBackgroundPoll(originalRequest)) {
          return Promise.reject(refreshError);
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Special logging for auth endpoints
    if (response.config.url?.includes('/auth/')) {
      console.log('✅ API Response Interceptor (AUTH):', {
        status: response.status,
        statusText: response.statusText,
        url: response.config.url,
        data: response.data,
        headers: {
          'set-cookie': response.headers['set-cookie'] ? 'Present' : 'Missing',
          'content-type': response.headers['content-type']
        },
        timestamp: new Date().toISOString()
      });
    } else {
      logger.debug(`✅ API Response from ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // Comprehensive error logging
    const errorLog = {
      errorType: error.constructor.name,
      errorName: error.name,
      errorMessage: error.message,
      timestamp: new Date().toISOString()
    };
    
    if (error.response) {
      // HTTP error response
      errorLog.response = {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: {
          'content-type': error.response.headers['content-type'],
          'set-cookie': error.response.headers['set-cookie'] ? 'Present' : 'Missing'
        }
      };
      errorLog.request = {
        method: error.config?.method,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        fullURL: `${error.config?.baseURL}${error.config?.url}`,
        withCredentials: error.config?.withCredentials
      };
      
      // Special logging for auth endpoints
      if (error.config?.url?.includes('/auth/')) {
        console.error('❌ API Response Interceptor Error (AUTH):', errorLog);
        console.error('❌ API Response Interceptor Error (AUTH) - Full Error JSON:', JSON.stringify(errorLog, null, 2));
      } else {
        logger.error('API Error:', error.response.status, error.response.data);
      }
    } else if (error.request) {
      // Request made but no response
      errorLog.request = {
        method: error.config?.method,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        fullURL: `${error.config?.baseURL}${error.config?.url}`,
        withCredentials: error.config?.withCredentials,
        timeout: error.config?.timeout
      };
      errorLog.networkError = true;
      
      if (error.config?.url?.includes('/auth/')) {
        console.error('❌ API Network Error (AUTH - No Response):', errorLog);
        console.error('❌ API Network Error (AUTH) - Full Error JSON:', JSON.stringify(errorLog, null, 2));
      } else {
        logger.error('API Network Error:', error);
      }
    } else {
      // Error in request setup
      errorLog.setupError = true;
      errorLog.message = error.message;
      
      if (error.config?.url?.includes('/auth/')) {
        console.error('❌ API Setup Error (AUTH):', errorLog);
        console.error('❌ API Setup Error (AUTH) - Full Error JSON:', JSON.stringify(errorLog, null, 2));
      } else {
        logger.error('API Network Error:', error);
      }
    }
    
    return Promise.reject(error);
  }
);

// =============================================================================
// MLB DATA API - NEW SECTION FOR DASHBOARD TILES
// =============================================================================

export const mlbAPI = {
  // Today's games with starting pitchers and live scores
  getTodaysGames: async () => {
    try {
      const response = await api.get('/api/mlb/games/today');
      return response.data;
    } catch (error) {
      console.error('Error fetching today\'s games:', error);
      return { success: false, games: [] };
    }
  },

  // MLB news headlines from RSS feeds
  getHeadlines: async () => {
    try {
      const response = await api.get('/api/mlb/news/headlines');
      return response.data;
    } catch (error) {
      console.error('Error fetching MLB headlines:', error);
      return { success: false, headlines: [] };
    }
  },

  // Current injury report
  getInjuryReport: async () => {
    try {
      const response = await api.get('/api/mlb/injuries');
      return response.data;
    } catch (error) {
      console.error('Error fetching injury report:', error);
      return { success: false, injuries: [] };
    }
  },

  getInjuryLookup: async () => {
    const response = await api.get('/api/mlb/injuries/lookup');
    return response.data;
  },

  getPlayerNotes: async (playerId) => {
    const params = playerId ? { player_id: playerId } : {};
    const response = await api.get('/api/mlb/news/player-notes', { params });
    return response.data;
  },

  // Trending players (hot/cold/waiver wire)
  getTrendingPlayers: async (queryString = '', leagueId = null) => {
    try {
      let url = `/api/mlb/trending${queryString}`;
      if (leagueId) {
        url += (queryString ? '&' : '?') + `league_id=${leagueId}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching trending players:', error);
      return { 
        success: false, 
        hot_players: [], 
        cold_players: [], 
        waiver_adds: [], 
        waiver_drops: [] 
      };
    }
  },

  // MLB API health check
  healthCheck: async () => {
    try {
      const response = await api.get('/api/mlb/health');
      return response.data;
    } catch (error) {
      console.error('Error checking MLB API health:', error);
      return { success: false, mlb_api_status: 'unhealthy' };
    }
  }
};

// =============================================================================
// AUTH API
// =============================================================================

export const authAPI = {
  signIn: async (email, password) => {
    console.log('🔐 authAPI.signIn: Starting sign-in request', {
      email: email?.toLowerCase()?.trim(),
      hasPassword: !!password,
      passwordLength: password?.length,
      timestamp: new Date().toISOString()
    });
    
    try {
      const requestData = { 
        email: email?.toLowerCase()?.trim(), 
        password 
      };
      
      console.log('🔐 authAPI.signIn: Sending request to /api/auth/login', {
        url: `${api.defaults.baseURL}/api/auth/login`,
        data: { ...requestData, password: '***REDACTED***' },
        withCredentials: api.defaults.withCredentials,
        headers: api.defaults.headers
      });
      
      const response = await api.post('/api/auth/login', requestData);
      
      console.log('✅ authAPI.signIn: Success response received', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: {
          'set-cookie': response.headers['set-cookie'] ? 'Present' : 'Missing',
          'content-type': response.headers['content-type']
        }
      });
      
      // Store access token if provided (for mobile clients)
      if (response.data?.access_token) {
        setStoredToken(response.data.access_token);
        console.log('🔐 authAPI.signIn: Access token stored for mobile');
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ authAPI.signIn: Error caught', {
        errorType: error.constructor.name,
        message: error.message,
        code: error.code,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        } : null,
        request: error.request ? {
          method: error.config?.method,
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          data: error.config?.data ? { ...JSON.parse(error.config.data), password: '***REDACTED***' } : null,
          headers: error.config?.headers
        } : null,
        stack: error.stack
      });
      
      // Re-throw to let calling code handle it
      throw error;
    }
  },
  
  signOut: async () => {
    try {
      // Clear stored token
      setStoredToken(null);
      const response = await api.post('/api/auth/logout');
      return response.data;
    } catch (error) {
      // Clear token even if logout fails
      setStoredToken(null);
      throw error;
    }
  },

  signUp: async (userData) => {
    const response = await api.post('/api/auth/signup', {
      email: userData.email,
      password: userData.password,
      firstName: userData.firstName,
      lastName: userData.lastName,
      favoriteTeam: userData.favoriteTeam
    });
    return response.data;
  },

  checkAuth: async () => {
    const response = await api.get('/api/auth/status');
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/api/auth/profile');
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await api.put('/api/auth/profile', profileData);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },

  verifyEmail: async (email, code) => {
    const response = await api.post('/api/auth/verify-email', { email, code });
    return response.data;
  },

  resendVerification: async (email) => {
    const response = await api.post('/api/auth/resend-verification', { email });
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (email, code, newPassword) => {
    const response = await api.post('/api/auth/reset-password', {
      email,
      code,
      password: newPassword
    });
    return response.data;
  },

  checkEmail: async (email) => {
    const response = await api.post('/api/auth/check-email', { email });
    return response.data;
  }
};

// =============================================================================
// ACCOUNT API
// =============================================================================

export const accountAPI = {
  // Profile Management
  getProfile: async () => {
    const response = await api.get('/api/account/profile');
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await api.put('/api/account/profile', profileData);
    return response.data;
  },

  // Profile Picture Upload
  getProfilePictureUploadUrl: async (filename, contentType) => {
    const response = await api.post('/api/account/get-profile-picture-upload-url', {
      filename: filename,
      content_type: contentType
    });
    return response.data;
  },

  uploadProfilePictureToS3: async (presignedUrl, file) => {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
    
    return { success: true, status: response.status };
  },

  uploadProfilePicture: async (file) => {
    try {
      console.log('📸 Starting profile picture upload for:', file.name);
      
      const urlResponse = await accountAPI.getProfilePictureUploadUrl(file.name, file.type);
      
      if (!urlResponse.success) {
        throw new Error(urlResponse.message || 'Failed to get upload URL');
      }
      
      await accountAPI.uploadProfilePictureToS3(urlResponse.upload_url, file);
      
      console.log('✅ Profile picture uploaded successfully to:', urlResponse.public_url);
      
      return {
        success: true,
        profile_picture_url: urlResponse.public_url,
        s3_key: urlResponse.s3_key
      };
      
    } catch (error) {
      console.error('❌ Profile picture upload failed:', error);
      throw error;
    }
  },

  // Welcome Settings
  getWelcomeSettings: async () => {
    const response = await api.get('/api/account/welcome-settings');
    return response.data;
  },

  updateWelcomeSettings: async (settings) => {
    const response = await api.put('/api/account/welcome-settings', settings);
    return response.data;
  },

  // Default League Preference (server-side)
  getDefaultLeague: async () => {
    try {
      const response = await api.get('/api/account/default-league');
      return response.data?.default_league_id || null;
    } catch (error) {
      console.error('Error fetching default league:', error);
      return null;
    }
  },

  setDefaultLeague: async (leagueId) => {
    try {
      const response = await api.put('/api/account/default-league', { league_id: leagueId });
      return response.data;
    } catch (error) {
      console.error('Error setting default league:', error);
    }
  },

  // Account Health Check
  healthCheck: async () => {
    const response = await api.get('/api/account/account-health');
    return response.data;
  }
};

// =============================================================================
// LEAGUES API
// =============================================================================

// Client-side whitelist for getFreeAgentsCanonical dateRange.
// 'custom' triggers backend's cross-DB aggregation path; requires both
// customStartDate + customEndDate (ISO YYYY-MM-DD).
// Per 9o.6.e Slice 2 Commit 8-rest: custom-window date semantic rules
// (end>=start, span<=365 days, no future) live in utils/customWindowValidator
// so SearchControls + MobileSearchControls + apiService share one rule set.
const FA_VALID_DATE_RANGES = new Set(['season', 'last7', 'last14', 'last30', 'custom']);

export const leaguesAPI = {
  // League Management
  getMyLeagues: async () => {
    const response = await api.get('/api/leagues/my-leagues');
    return response.data;
  },

  createLeague: async (leagueData) => {
    const response = await api.post('/api/leagues/create', leagueData);
    return response.data;
  },

  joinPrivateLeague: async (leagueCode) => {
    try {
      const response = await api.post('/api/leagues/join-private', {
        league_code: leagueCode.toUpperCase()
      });
      return response.data;
    } catch (error) {
      console.error('Error joining private league:', error);
      if (error.response?.status === 404) {
        throw new Error('League code not found');
      } else if (error.response?.status === 400) {
        throw new Error('Invalid league code format');
      } else if (error.response?.status === 409) {
        throw new Error('League is full or you are already a member');
      }
      throw new Error('Failed to join private league');
    }
  },

  // DD-JOIN-PUBLIC-FIX-0728: surface the SERVER'S reason, don't invent one.
  // This used to map 404/409/403 to hand-written strings and collapse everything else onto
  // "Failed to join league" — which swallowed all THREE distinct 400s the endpoint returns
  // (not accepting new teams / you already have a team / league is full), the exact messages a
  // user needs. The 409 branch was dead: join-public never returns 409; "full" and "already a
  // member" are 400s, so their real text was the text being discarded. formatApiError
  // (services/errorUtils.js) already extracts `detail` from the FastAPI HTTPException envelope
  // this route uses — reuse it rather than re-deriving messages here (Rule 11). The fallback
  // is only reached when the server sent no message at all (e.g. a network failure).
  joinPublicLeague: async (leagueId) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/join-public`);
      return response.data;
    } catch (error) {
      console.error('Error joining public league:', error);
      throw new Error(formatApiError(error, 'Failed to join league'));
    }
  },

  checkLeagueCreationStatus: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/creation-status`);
    return response.data;
  },

  getLeagueDetails: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}`);
    return response.data;
  },

  deleteLeague: async (leagueId) => {
    const response = await api.delete(`/api/leagues/${leagueId}`);
    return response.data;
  },

  // League Status Management
  getLeagueStatus: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/status`);
    return response.data;
  },

  updateLeagueStatus: async (leagueId, newStatus) => {
    const response = await api.put(`/api/leagues/${leagueId}/status`, { 
      league_status: newStatus 
    });
    return response.data;
  },

  setDraftType: async (leagueId, draftType) => {
    const response = await api.put(`/api/leagues/${leagueId}/draft-type`, { 
      draft_type: draftType 
    });
    return response.data;
  },

  startSeason: async (leagueId) => {
    const response = await api.post(`/api/leagues/${leagueId}/start-season`);
    return response.data;
  },

  endSeason: async (leagueId) => {
    const response = await api.post(`/api/leagues/${leagueId}/end-season`);
    return response.data;
  },

  checkPriceStatus: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/price-status`);
    return response.data;
  },

  // Pricing Engine
  getPricingData: async (leagueId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/salaries/pricing-data`);
      return response.data;
    } catch (error) {
      console.error('Error fetching pricing data:', error);
      throw error;
    }
  },

  // Market Values (Phase 3 - current market values with baseline comparison)
  getMarketValues: async (leagueId, playerIds = null) => {
    try {
      const url = playerIds
        ? `/api/leagues/${leagueId}/salaries/market-values?player_ids=${playerIds}`
        : `/api/leagues/${leagueId}/salaries/market-values`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching market values:', error);
      throw error;
    }
  },

  // Salary & Contract Settings
  getPlayerPrices: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/salaries/prices`);
    return response.data;
  },

  getSalarySettings: async (leagueId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/salaries/settings`);
      return response.data;
    } catch (error) {
      console.error('Error fetching salary settings:', error);
      return { 
        success: true,
        settings: {
          use_dual_cap: true,
          draft_cap: 600,
          season_cap: 200,
          salary_cap: 800,
          min_salary: 2,
          salary_increment: 2,
          rookie_price: 20,
          draft_cap_usage: 0.75,
          hitting_categories: ['R', 'RBI', 'HR', 'SB', 'AVG', 'OPS'],
          pitching_categories: ['W', 'QS', 'K', 'S', 'ERA', 'WHIP']
        }
      };
    }
  },

  updateSalarySettings: async (leagueId, settings) => {
    const response = await api.post(`/api/leagues/${leagueId}/salaries/prices`, settings);
    return response.data;
  },

  startAsyncPriceSave: async (leagueId, settings) => {
    const response = await api.post(`/api/leagues/${leagueId}/salaries/prices/async`, settings);
    return response.data;
  },

  checkPriceSaveStatus: async (leagueId, jobId) => {
    const response = await api.get(`/api/leagues/${leagueId}/salaries/job/${jobId}`);
    return response.data;
  },

  pollPriceSaveJob: async (leagueId, jobId, options = {}) => {
    const { 
      onProgress, 
      pollInterval = 2000, 
      maxAttempts = 150
    } = options;
    
    let attempts = 0;
    
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++;
          const status = await leaguesAPI.checkPriceSaveStatus(leagueId, jobId);
          
          if (onProgress) {
            onProgress(status);
          }
          
          if (status.status === 'completed') {
            resolve(status);
          } else if (status.status === 'failed') {
            reject(new Error(status.error || 'Price save failed'));
          } else if (attempts >= maxAttempts) {
            reject(new Error('Price save timed out'));
          } else {
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      poll();
    });
  },

  savePrices: async (leagueId, prices, settings, method = 'adaptive') => {
    const response = await api.post(`/api/leagues/${leagueId}/save-prices`, {
      prices,
      settings,
      method
    });
    return response.data;
  },

  resetPrices: async (leagueId) => {
    const response = await api.post(`/api/leagues/${leagueId}/reset-prices`);
    return response.data;
  },

  // Pricing Logic Settings
  getPricingLogicSettings: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/salaries/pricing-logic-settings`);
    return response.data;
  },

  savePricingLogicSettings: async (leagueId, settings) => {
    const response = await api.post(`/api/leagues/${leagueId}/salaries/pricing-logic-settings`, settings);
    return response.data;
  },

  getPricingScenarios: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/salaries/pricing-scenarios`);
    return response.data;
  },

  savePricingScenario: async (leagueId, scenario) => {
    const response = await api.post(`/api/leagues/${leagueId}/salaries/pricing-scenarios`, scenario);
    return response.data;
  },

  deletePricingScenario: async (leagueId, scenarioId) => {
    const response = await api.delete(`/api/leagues/${leagueId}/salaries/pricing-scenarios/${scenarioId}`);
    return response.data;
  },

  // Server-Side Price Generation (NEW - Migrated from client-side engine)
  generatePlayerPrices: async (leagueId, customPricingLogic = null) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/salaries/generate-prices`, {
        custom_pricing_logic: customPricingLogic
      });
      return response.data;
    } catch (error) {
      console.error('Error generating player prices:', error);
      throw error;
    }
  },

  // League Settings & Info
  getLeagueSettings: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/settings`);
    return response.data;
  },

  // The VIEWER's team in this league (server resolves from the auth user).
  // Added DD-FIXES Bug 5 for the card's roster-fit fetch — the surface only
  // needs { team_id, team_name, is_commissioner, ... }.
  getMyTeam: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/my-team`);
    return response.data;
  },

  getLeagueOwners: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/owners`);
    return response.data;
  },

  fillLeagueToCapacity: async (leagueId) => {
    const response = await api.post(`/api/leagues/${leagueId}/teams/fill-to-capacity`);
    return response.data;
  },

  getLeagueStandings: async (leagueId, period, throughDate) => {
    const params = {};
    if (period) params.period = period;
    if (throughDate) params.through_date = throughDate;
    const response = await api.get(`/api/leagues/${leagueId}/standings`, { params });
    return response.data;
  },

  getStandingsAvailableDates: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/standings/available-dates`);
    return response.data;
  },

  overrideSeasonStandings: async (leagueId, year, standings) => {
    const response = await api.post(`/api/leagues/${leagueId}/seasons/${year}/standings/override`, { standings });
    return response.data;
  },

  // Fantasy Impact — authoritative per-category rotisserie standings for one team.
  // DD-RO-GAPS-0727: basis = 'current' (default, authoritative accrued standings), 'projected' (accrued+ROS,
  // current roster), or 'projected_selection' (with selectionPlayerId added to the user's roster).
  // DD-RO-STANDINGS-FN-CRASH-0728: this lives on leaguesAPI (its endpoint is /api/leagues/...) — every
  // optimizer consumer (OptimizerDashboard sidebar, OptimizerGapAnalysis tab, MobileOptimizer) calls it as
  // leaguesAPI.getTeamCategoryStandings. It was originally added to playersAPI by mistake, so all three
  // crashed on load ("getTeamCategoryStandings is not a function"); the callers' tests only passed via mocks.
  getTeamCategoryStandings: async (leagueId, teamId, basis = 'current', selectionPlayerId = null, subjectPlayerId = null) => {
    const params = { basis };
    if (selectionPlayerId != null) params.selection_player_id = selectionPlayerId;
    // DD-RO-GAPS-CLUSTER-0728 4.2: the with-selection swap is subject-OUT / selection-IN — pass the flyout
    // subject (the player being replaced) so the reprojection drops it, not "add and let 2A displace a bench".
    if (subjectPlayerId != null) params.subject_player_id = subjectPlayerId;
    const response = await api.get(`/api/leagues/${leagueId}/teams/${teamId}/category-standings`, { params });
    return response.data;
  },

  getSeasonHistory: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/seasons/history`);
    return response.data;
  },

  getUserTeam: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/my-team?t=${Date.now()}`);
    return response.data;
  },

  // Team Management & Logo Upload
  setupTeam: async (leagueId, teamData) => {
    const response = await api.post(`/api/leagues/${leagueId}/setup-team`, teamData);
    return response.data;
  },

  getLogoUploadUrl: async (leagueId, filename, contentType) => {
    const response = await api.post(`/api/leagues/${leagueId}/upload-logo-url`, {
      filename: filename,
      content_type: contentType
    });
    return response.data;
  },

  uploadLogoToS3: async (presignedUrl, file) => {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
    
    return { success: true, status: response.status };
  },

  uploadTeamLogo: async (leagueId, file) => {
    try {
      console.log('🖼️ Starting team logo upload for:', file.name);
      
      const urlResponse = await leaguesAPI.getLogoUploadUrl(leagueId, file.name, file.type);
      
      if (!urlResponse.success) {
        throw new Error(urlResponse.message || 'Failed to get upload URL');
      }
      
      await leaguesAPI.uploadLogoToS3(urlResponse.upload_url, file);
      
      console.log('✅ Logo uploaded successfully to:', urlResponse.public_url);
      
      return {
        success: true,
        logo_url: urlResponse.public_url,
        s3_key: urlResponse.s3_key
      };
      
    } catch (error) {
      console.error('❌ Logo upload failed:', error);
      throw error;
    }
  },

  deleteTeamLogo: async (leagueId) => {
    const response = await api.delete(`/api/leagues/${leagueId}/team-logo`);
    return response.data;
  },

  // League Banner Upload
  getLeagueBannerUploadUrl: async (leagueId, filename, contentType) => {
    const response = await api.post(`/api/leagues/${leagueId}/upload-banner-url`, {
      filename: filename,
      content_type: contentType
    });
    return response.data;
  },

  uploadBannerToS3: async (presignedUrl, file) => {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
    
    return { success: true, status: response.status };
  },

  uploadLeagueBanner: async (leagueId, file) => {
    try {
      console.log('🏆 Starting league banner upload for:', file.name);
      
      const urlResponse = await leaguesAPI.getLeagueBannerUploadUrl(leagueId, file.name, file.type);
      
      if (!urlResponse.success) {
        throw new Error(urlResponse.message || 'Failed to get upload URL');
      }
      
      await leaguesAPI.uploadBannerToS3(urlResponse.upload_url, file);
      
      console.log('✅ League banner uploaded successfully to:', urlResponse.public_url);
      
      return {
        success: true,
        banner_url: urlResponse.public_url,
        s3_key: urlResponse.s3_key
      };
      
    } catch (error) {
      console.error('❌ League banner upload failed:', error);
      throw error;
    }
  },

  updateLeagueSettings: async (leagueId, settings) => {
    const response = await api.put(`/api/leagues/${leagueId}/settings`, settings);
    return response.data;
  },

  // Team Browsing & Roster Management
  getLeagueTeams: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/teams`);
    return response.data;
  },

  getMyRoster: async (leagueId, options = {}) => {
    let url = `/api/leagues/${leagueId}/my-roster`;
    
    if (options.commissioner_action && options.target_team_id) {
      const params = new URLSearchParams({
        target_team_id: options.target_team_id,
        commissioner_action: 'true'
      });
      url += `?${params.toString()}`;
    }
    
    const response = await api.get(url);
    return response.data;
  },

  // CANONICAL ROSTER ENDPOINTS
  getMyRosterCanonical: async (leagueId, options = {}) => {
    let url = `/api/players/leagues/${leagueId}/my-roster`;
    
    if (options.commissioner_action && options.target_team_id) {
      const params = new URLSearchParams({
        target_team_id: options.target_team_id,
        commissioner_action: 'true'
      });
      url += `?${params.toString()}`;
    }
    
    const response = await api.get(url);
    return response.data;
  },

  getTeamRosterCanonical: async (leagueId, teamId) => {
    const response = await api.get(`/api/players/leagues/${leagueId}/teams/${teamId}/roster`);
    return response.data;
  },

  moveRosterPlayer: async (leagueId, moveData) => {
    const response = await api.post(`/api/leagues/${leagueId}/move-player`, moveData);
    return response.data;
  },

  addPlayerToTeam: async (leagueId, playerData) => {
    // Bible Rule 9: API boundary validation — throw on missing required fields
    // rather than silently default. Defense-in-depth: callers should already
    // have thrown, but if they didn't, fail loudly here rather than send a
    // malformed payload that hits backend Pydantic and returns generic 422.
    if (!playerData.league_player_id) {
      throw new Error('addPlayerToTeam: league_player_id is required');
    }
    if (playerData.salary === undefined || playerData.salary === null) {
      throw new Error(
        'addPlayerToTeam: salary is required (caller must resolve canonical ' +
        'baseline_price or set 0 for minors)'
      );
    }
    if (playerData.contract_years === undefined || playerData.contract_years === null) {
      throw new Error(
        'addPlayerToTeam: contract_years is required (caller must resolve from ' +
        'canonical financial or set 0 for minors)'
      );
    }
    if (!playerData.roster_status) {
      throw new Error('addPlayerToTeam: roster_status is required');
    }

    const requestData = {
      league_player_id: playerData.league_player_id,
      salary: playerData.salary,
      contract_years: playerData.contract_years,
      roster_status: playerData.roster_status,
      // roster_position is legitimately null for non-active slots (bench/minors)
      roster_position: playerData.roster_position ?? null
    };

    if (playerData.drop_player_id) {
      requestData.drop_player_id = playerData.drop_player_id;
    }

    if (playerData.commissioner_action && playerData.target_team_id) {
      requestData.commissioner_action = true;
      requestData.target_team_id = playerData.target_team_id;
    }
    const response = await api.post(`/api/leagues/${leagueId}/add-player`, requestData);
    return response.data;
  },

  getDropImpact: async (leagueId, leaguePlayerId) => {
    const response = await api.get(
      `/api/leagues/${leagueId}/players/${leaguePlayerId}/drop-impact`
    );
    return response.data;
  },

  getLeagueWaivers: (leagueId) =>
    api.get(`/api/leagues/${leagueId}/waivers`),

  getMyWaiverClaims: (leagueId) =>
    api.get(`/api/leagues/${leagueId}/waivers/my-claims`),

  getWaiverPriority: (leagueId) =>
    api.get(`/api/leagues/${leagueId}/waivers/priority`),

  submitWaiverClaim: (leagueId, claimData) =>
    api.post(`/api/leagues/${leagueId}/waivers/claims`, claimData),

  cancelWaiverClaim: (leagueId, claimId) =>
    api.delete(`/api/leagues/${leagueId}/waivers/claims/${claimId}`),

  dropPlayerFromTeam: async (leagueId, leaguePlayerId, options = {}) => {
    const requestData = {
      league_player_id: leaguePlayerId
    };

    if (options.commissioner_action && options.target_team_id) {
      requestData.commissioner_action = true;
      requestData.target_team_id = options.target_team_id;
    }

    const response = await api.post(`/api/leagues/${leagueId}/drop-player`, requestData);
    return response.data;
  },

  // Drop-shuffle plan endpoints (Phase 2.1B / 2.2)
  // Per ROSTER_PLAN_SPEC.md § B.5.4
  /**
   * Generate plans for a user-chosen drop.
   * @param {string} leagueId
   * @param {Object} body
   * @param {number} body.candidate_player_id - MLB player ID
   * @param {number} body.drop_player_id - MLB player ID
   * @param {boolean} [body.commissioner_action]
   * @param {string|null} [body.target_team_id]
   * @returns {Promise<Object>} { success, candidate, drop_player, plans, infeasible_reason? }
   */
  generatePlanForDrop: async (leagueId, body) => {
    const response = await api.post(`/api/leagues/${leagueId}/plan-add`, body);
    return response.data;
  },

  /**
   * Execute a chosen plan from generatePlanForDrop.
   * @param {string} leagueId
   * @param {Object} body
   * @param {number} body.candidate_player_id - MLB player ID
   * @param {number} body.drop_player_id - MLB player ID
   * @param {string} body.plan_fingerprint - sha256 from PlanCandidate
   * @param {boolean} [body.commissioner_action]
   * @param {string|null} [body.target_team_id]
   * @returns {Promise<Object>} {success, plan_id, transaction_ids, should_dispatch_roster_updated, updated_roster} on success;
   *                            {success: false, error_class, error_message, rollback_performed} on failure
   */
  executePlan: async (leagueId, body) => {
    const response = await api.post(`/api/leagues/${leagueId}/execute-plan`, body);
    return response.data;
  },

  // Minor League Draft - Phase 5
  initializeMinorLeagueDraft: async (leagueId, draftYear, totalRounds = 2) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/minor-league-draft`, {
        draft_year: draftYear,
        total_rounds: totalRounds
      });
      return response.data;
    } catch (error) {
      console.error('Error initializing minor league draft:', error);
      throw error;
    }
  },

  getMinorLeagueDraft: async (leagueId, draftYear = null) => {
    try {
      let url = `/api/leagues/${leagueId}/minor-league-draft`;
      if (draftYear) {
        url += `?draft_year=${draftYear}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error getting minor league draft:', error);
      throw error;
    }
  },

  makeMinorLeagueDraftPick: async (leagueId, playerName, mlbTeam = null, position = null) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/minor-league-draft/pick`, {
        player_name: playerName,
        mlb_team: mlbTeam,
        position: position
      });
      return response.data;
    } catch (error) {
      console.error('Error making minor league draft pick:', error);
      throw error;
    }
  },

  makePhantomDraftPick: async (leagueId, playerName, mlbTeam = null, position = null) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/minor-league-draft/pick-phantom`, {
        player_name: playerName,
        mlb_team: mlbTeam,
        position: position,
        confirmed: true
      });
      return response.data;
    } catch (error) {
      console.error('Error making phantom draft pick:', error);
      throw error;
    }
  },

  getMinorLeagueDraftPicks: async (leagueId, draftYear = null) => {
    try {
      let url = `/api/leagues/${leagueId}/minor-league-draft/picks`;
      if (draftYear) {
        url += `?draft_year=${draftYear}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error getting minor league draft picks:', error);
      throw error;
    }
  },

  // Live Draft API Methods
  getLiveDraftConnection: async (leagueId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/draft/live/connection`);
      return response.data;
    } catch (error) {
      console.error('Error getting live draft connection:', error);
      throw error;
    }
  },

  getLiveDraftState: async (leagueId, year = null) => {
    try {
      let url = `/api/leagues/${leagueId}/draft/live/state`;
      if (year) {
        url += `?year=${year}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error getting live draft state:', error);
      throw error;
    }
  },

  getSuggestedPick: async (leagueId, teamId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/draft/live/suggest-pick?team_id=${teamId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting suggested pick:', error);
      return { success: false, suggestions: [] };
    }
  },

  startLiveDraft: async (leagueId, year = null) => {
    try {
      let url = `/api/leagues/${leagueId}/draft/live/start`;
      if (year) {
        url += `?year=${year}`;
      }
      const response = await api.post(url);
      return response.data;
    } catch (error) {
      console.error('Error starting live draft:', error);
      throw error;
    }
  },

  stopLiveDraft: async (leagueId, year = null) => {
    try {
      let url = `/api/leagues/${leagueId}/draft/live/stop`;
      if (year) {
        url += `?year=${year}`;
      }
      const response = await api.post(url);
      return response.data;
    } catch (error) {
      console.error('Error stopping live draft:', error);
      throw error;
    }
  },

  // Get draft order (all picks with team assignments)
  getDraftOrder: async (leagueId, season = null) => {
    try {
      let url = `/api/leagues/${leagueId}/draft/order`;
      if (season) {
        url += `?season=${season}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error getting draft order:', error);
      throw error;
    }
  },

  // Commissioner direct pick reassignment (no trade record)
  reassignPick: async (leagueId, pickId, newTeamId) => {
    const response = await api.patch(`/api/leagues/${leagueId}/draft/picks/${pickId}/reassign`, {
      new_team_id: newTeamId
    });
    return response.data;
  },

  // Generate draft order (assigns overall_pick_number to all picks)
  generateDraftOrder: async (leagueId, method = 'snake', manualOrder = null) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/draft/generate`, {
        method: method, // 'snake', 'linear', 'manual'
        manual_order: manualOrder // Optional: array of team_ids for manual order
      });
      return response.data;
    } catch (error) {
      console.error('Error generating draft order:', error);
      throw error;
    }
  },

  // Generate draft order: Round 1 via lottery or manual; Rounds 2+ snake or linear
  generateDraftOrderR1R2: async (leagueId, round1Method, rounds2PlusMethod, manualOrder = null) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/draft/generate`, {
        method: 'r1_method_r2plus_derive',
        round1_method: round1Method,      // 'manual' | 'lottery'
        rounds_2plus_method: rounds2PlusMethod,  // 'snake' | 'linear'
        manual_order: manualOrder         // required when round1_method = 'manual'
      });
      return response.data;
    } catch (error) {
      console.error('Error generating R1+R2+ draft order:', error);
      throw error;
    }
  },

  // Get available players for draft (excludes already drafted)
  // Uses current_season from league (no year parameter needed)
  getAvailablePlayersForDraft: async (leagueId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      // No year parameter - endpoint uses current_season from league
      if (filters.position) params.append('position', filters.position);
      if (filters.search) params.append('search', filters.search);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.sort_order) params.append('sort_order', filters.sort_order);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);
      
      const response = await api.get(`/api/leagues/${leagueId}/draft/live/available-players?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching available players for draft:', error);
      throw error;
    }
  },

  completeMinorLeagueDraft: async (leagueId, draftYear = null) => {
    try {
      let url = `/api/leagues/${leagueId}/minor-league-draft/complete`;
      if (draftYear) {
        url += `?draft_year=${draftYear}`;
      }
      const response = await api.post(url);
      return response.data;
    } catch (error) {
      console.error('Error completing minor league draft:', error);
      throw error;
    }
  },

  searchMinorLeaguePlayers: async (leagueId, query, mlbTeam = null, limit = 20) => {
    try {
      let url = `/api/leagues/${leagueId}/minor-league-draft/search?query=${encodeURIComponent(query)}`;
      if (mlbTeam) {
        url += `&mlb_team=${encodeURIComponent(mlbTeam)}`;
      }
      if (limit) {
        url += `&limit=${limit}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error searching minor league players:', error);
      return [];
    }
  },

  startMinorLeagueDraft: async (leagueId, draftYear = null) => {
    try {
      let url = `/api/leagues/${leagueId}/minor-league-draft/start`;
      if (draftYear) url += `?draft_year=${draftYear}`;
      const response = await api.post(url);
      return response.data;
    } catch (error) {
      console.error('Error starting minor league draft:', error);
      throw error;
    }
  },

  undoMinorLeagueDraftPick: async (leagueId, draftYear = null) => {
    try {
      let url = `/api/leagues/${leagueId}/minor-league-draft/undo-pick`;
      if (draftYear) url += `?draft_year=${draftYear}`;
      const response = await api.post(url);
      return response.data;
    } catch (error) {
      console.error('Error undoing minor league draft pick:', error);
      throw error;
    }
  },

  commissionerMinorLeagueDraftPick: async (leagueId, playerName, teamId, mlbTeam = null, position = null) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/minor-league-draft/pick`, {
        player_name: playerName,
        team_id: teamId,
        mlb_team: mlbTeam,
        position: position
      });
      return response.data;
    } catch (error) {
      console.error('Error making commissioner draft pick:', error);
      throw error;
    }
  },

  setMinorLeagueDraftOrder: async (leagueId, method, manualOrder = null) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/minor-league-draft/order`, {
        method,
        manual_order: manualOrder
      });
      return response.data;
    } catch (error) {
      console.error('Error setting minor league draft order:', error);
      throw error;
    }
  },

  // Commissioner force-override of ML draft order — no draft_status guard
  commissionerOverrideMLDraftOrder: async (leagueId, method, manualOrder = null) => {
    try {
      const response = await api.patch(`/api/leagues/${leagueId}/minor-league-draft/order/override`, {
        method,
        manual_order: manualOrder
      });
      return response.data;
    } catch (error) {
      console.error('Error force-overriding ML draft order:', error);
      throw error;
    }
  },

  // Commissioner: reassign a specific ML draft pick slot to a different team
  reassignMLPickSlot: async (leagueId, roundNumber, pickInRound, newTeamId, draftYear = null) => {
    let url = `/api/leagues/${leagueId}/minor-league-draft/pick-slots/reassign`;
    if (draftYear) url += `?draft_year=${draftYear}`;
    const response = await api.patch(url, {
      round_number: roundNumber,
      pick_in_round: pickInRound,
      new_team_id: newTeamId
    });
    return response.data;
  },

  // Commissioner: ensure ML draft rows exist for every year in the tradeable pick window
  ensureMLDraftWindow: async (leagueId) => {
    const response = await api.post(`/api/leagues/${leagueId}/minor-league-draft/ensure-window`);
    return response.data;
  },

  setMinorLeagueDraftClock: async (leagueId, pickTimeLimitMinutes = null) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/minor-league-draft/clock`, {
        pick_time_limit_minutes: pickTimeLimitMinutes
      });
      return response.data;
    } catch (error) {
      console.error('Error setting minor league draft clock:', error);
      throw error;
    }
  },

  checkMyMinorLeagueDraftTurn: async (leagueId, draftYear = null) => {
    try {
      let url = `/api/leagues/${leagueId}/minor-league-draft/my-turn`;
      if (draftYear) {
        url += `?draft_year=${draftYear}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error checking draft turn:', error);
      throw error;
    }
  },

  // Draft Notifications
  getDraftNotificationCount: async (leagueId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/minor-league-draft/notifications/count`);
      return response.data;
    } catch (error) {
      console.error('Error getting draft notification count:', error);
      return { unread_count: 0 };
    }
  },

  getDraftNotifications: async (leagueId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/minor-league-draft/notifications`);
      return response.data;
    } catch (error) {
      console.error('Error getting draft notifications:', error);
      return { notifications: [], unread_count: 0 };
    }
  },

  markDraftNotificationsRead: async (leagueId) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/minor-league-draft/notifications/mark-read`);
      return response.data;
    } catch (error) {
      console.error('Error marking draft notifications read:', error);
      return { success: false, marked_count: 0 };
    }
  },

  getDraftNotificationPreferences: async (leagueId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/minor-league-draft/notifications/preferences`);
      return response.data;
    } catch (error) {
      console.error('Error getting notification preferences:', error);
      return { success: true, preferences: {} };
    }
  },

  updateDraftNotificationPreferences: async (leagueId, preferences) => {
    try {
      const response = await api.put(`/api/leagues/${leagueId}/minor-league-draft/notifications/preferences`, preferences);
      return response.data;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  },

  // General Notifications (waiver, trade, system)
  getNotifications: async (leagueId, category) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/notifications`, { params: { category } });
      return response.data;
    } catch (error) {
      console.error('Error getting notifications:', error);
      return { notifications: [], unread_count: 0 };
    }
  },

  getNotificationCount: async (leagueId, category) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/notifications/count`, { params: { category } });
      return response.data;
    } catch (error) {
      console.error('Error getting notification count:', error);
      return { unread_count: 0 };
    }
  },

  markNotificationRead: async (leagueId, notifId) => {
    try {
      const response = await api.put(`/api/leagues/${leagueId}/notifications/${notifId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification read:', error);
      return { success: false };
    }
  },

  markNotificationsRead: async (leagueId, category) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/notifications/mark-read`, null, { params: { category } });
      return response.data;
    } catch (error) {
      console.error('Error marking notifications read:', error);
      return { success: false, marked_count: 0 };
    }
  },

  getNotificationPreferences: async (leagueId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/notifications/preferences`);
      return response.data;
    } catch (error) {
      console.error('Error getting notification preferences:', error);
      return { success: true, preferences: {} };
    }
  },

  updateNotificationPreferences: async (leagueId, preferences) => {
    try {
      const response = await api.put(`/api/leagues/${leagueId}/notifications/preferences`, preferences);
      return response.data;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  },

  // Player in League Context - CANONICAL (NEW)
  getPlayerInLeague: async (leagueId, playerId) => {
    try {
      const response = await api.get(`/api/players/leagues/${leagueId}/players/${playerId}`);
      // Response: { success: true, player: { ids: {...}, info: {...}, league_context: {...}, stats: {...} } }
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching player in league:', error);
      throw error;
    }
  },

  // Alias for getPlayerInLeague (used by TradeCenter and other components)
  getPlayerCanonical: async (leagueId, playerId) => {
    return await leaguesAPI.getPlayerInLeague(leagueId, playerId);
  },

  // Free Agents - CANONICAL (NEW)
  getFreeAgentsCanonical: async (leagueId, filters = {}) => {
    try {
      // Defense-in-depth — backend router (Commit 4) is the source of truth, but
      // catching invalid values here avoids a network round trip on typos.
      const dateRange = filters.dateRange !== undefined ? filters.dateRange : 'season';
      if (!FA_VALID_DATE_RANGES.has(dateRange)) {
        throw new Error(
          `Invalid dateRange '${dateRange}'. Expected one of: season, last7, last14, last30, custom.`
        );
      }

      // 9o.6.e Slice 2: custom-window date params. Required when dateRange='custom';
      // rejected for non-custom (backend router enforces the same rule).
      // Commit 8-rest: validation delegated to utils/customWindowValidator
      // (shared with SearchControls/MobileSearchControls for inline UI feedback).
      const customStartDate = filters.customStartDate ?? null;
      const customEndDate = filters.customEndDate ?? null;
      if (dateRange === 'custom') {
        const validation = validateCustomWindow(customStartDate, customEndDate);
        if (!validation.valid) {
          throw new Error(validation.throwMessage);
        }
      } else if (customStartDate || customEndDate) {
        throw new Error(
          `customStartDate/customEndDate are only valid with dateRange='custom'. Got '${dateRange}'.`
        );
      }

      const params = new URLSearchParams();
      if (filters.position) params.append('position', filters.position);
      if (filters.search) params.append('search', filters.search);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      // FIXED: Convert show_all (frontend) to available_only (backend)
      if (filters.show_all !== undefined) params.append('available_only', !filters.show_all);
      // Historical stats toggle - if true, show previous season stats
      if (filters.is_historical) params.append('is_historical', 'true');
      // Commissioner: include non-40-man players
      if (filters.include_non_40man) params.append('include_non_40man', 'true');
      // 9o.6.e: stats window for sort selection. Always sent (default 'season').
      params.append('date_range', dateRange);
      // Slice 2: forward custom-window dates when in custom mode.
      if (dateRange === 'custom') {
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
      }

      const queryString = params.toString();
      const url = queryString
        ? `/api/players/leagues/${leagueId}/free-agents?${queryString}`
        : `/api/players/leagues/${leagueId}/free-agents`;

      console.log('🔗 FA SEARCH URL:', url);
      const response = await api.get(url);
      // Response: { success: true, players: [...], count: 500, pagination: {...} }
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching free agents:', error);
      throw error;
    }
  },

  /**
   * Get free agents with stats (CANONICAL)
   * Explicit wrapper that ONLY returns free agents
   * @returns {Promise<{success: boolean, players: Array, total_count: number, has_more: boolean}>}
   */
  getFreeAgentsWithStats: async (leagueId, params = {}) => {
    return await leaguesAPI.getFreeAgentsCanonical(leagueId, {
      position: params.position,
      search: params.search,
      sort_by: params.sortBy || params.sort_by,
      limit: params.limit || 500,
      offset: params.offset || 0,
      show_all: false  // Explicit: free agents only
    });
  },

  /**
   * Get free agent spotlight — z-score based upgrade recommendations
   * @returns {Promise<{success: boolean, upgrades: Array}>}
   */
  getFreeAgentSpotlight: async (leagueId) => {
    try {
      const response = await api.get(`/api/players/leagues/${leagueId}/free-agents/spotlight`);
      return response.data;
    } catch (error) {
      console.error('Error fetching FA spotlight:', error);
      return { success: false, upgrades: [] };
    }
  },

  /**
   * Get all players (including owned) with stats (CANONICAL)
   * Explicit wrapper that returns ALL players in the league
   * @returns {Promise<{success: boolean, players: Array, total_count: number, has_more: boolean}>}
   */
  getAllPlayersWithStats: async (leagueId, params = {}) => {
    return await leaguesAPI.getFreeAgentsCanonical(leagueId, {
      position: params.position,
      search: params.search,
      sort_by: params.sortBy || params.sort_by,
      limit: params.limit || 500,
      offset: params.offset || 0,
      show_all: true  // Explicit: all players
    });
  },

  // Free Agents - LEGACY (will be deprecated)
  getFreeAgents: async (leagueId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.position) params.append('position', filters.position);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    
    const queryString = params.toString();
    const url = queryString 
      ? `/api/leagues/${leagueId}/free-agents?${queryString}` 
      : `/api/leagues/${leagueId}/free-agents`;
    
    const response = await api.get(url);
    return response.data;
  },

  getFreeAgentsEnhanced: async (leagueId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.position) params.append('position', filters.position);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    if (filters.show_all !== undefined) params.append('show_all', filters.show_all);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);
    
    const queryString = params.toString();
    const url = queryString 
      ? `/api/leagues/${leagueId}/free-agents-enhanced?${queryString}` 
      : `/api/leagues/${leagueId}/free-agents-enhanced`;
    
    const response = await api.get(url);
    return response.data;
  },

  // All League Players (owned + free agents for search/trade)
  getAllLeaguePlayers: async (leagueId, filters = {}, options = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.position) params.append('position', filters.position);
      if (filters.search) params.append('search', filters.search);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);

      const queryString = params.toString();
      const url = queryString
        ? `/api/leagues/${leagueId}/all-league-players?${queryString}`
        : `/api/leagues/${leagueId}/all-league-players`;

      // DD-FASEARCH-RACE-0730: optional AbortSignal so the caller can cancel a
      // superseded in-flight search (backward compatible — omit to keep prior behavior).
      const response = await api.get(url, options.signal ? { signal: options.signal } : undefined);
      // Response: { success: true, players: [...], count: N }
      // Returns ALL players in league (owned + free agents) with full stats
      return response.data;
    } catch (error) {
      console.error('Error fetching all league players:', error);
      throw error;
    }
  },

  // Player Stats
  getLeaguePlayerStats: async (leagueId, playerId) => {
    const response = await api.get(`/api/leagues/${leagueId}/players/${playerId}/stats`);
    return response.data;
  },

  getLeaguePlayerGameLogs: async (leagueId, playerId) => {
    const response = await api.get(`/api/leagues/${leagueId}/players/${playerId}/game-logs`);
    return response.data;
  },

  // getPlayerContract — KILLED in Phase 4: reads from /modal player.league_context.financial

  // Transactions
  getTransactions: async (leagueId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
    if (filters.days_back) params.append('days_back', filters.days_back);
    if (filters.limit) params.append('limit', filters.limit);
    
    const queryString = params.toString();
    const url = queryString 
      ? `/api/leagues/${leagueId}/transactions?${queryString}` 
      : `/api/leagues/${leagueId}/transactions`;
    
    const response = await api.get(url);
    return response.data;
  },

  processTransaction: async (leagueId, transactionData) => {
    const response = await api.post(`/api/leagues/${leagueId}/transactions`, transactionData);
    return response.data;
  },

  logTransaction: async (leagueId, transactionData) => {
    const response = await api.post(`/api/leagues/${leagueId}/log-transaction`, transactionData);
    return response.data;
  },

  // PHASE 3: Transaction Reversal (Commissioner Only)
  reverseTransaction: async (leagueId, transactionId) => {
    try {
      const response = await api.delete(`/api/leagues/${leagueId}/transactions/${transactionId}`);
      return response.data;
    } catch (error) {
      console.error('Error reversing transaction:', error);
      throw error;
    }
  },

  // R.0 STEP 3.E: Stats rebuild retry endpoint. Idempotent retry per
  // Bible § 4.5.13.7 single-source-ID contract — passes only
  // (leagueId, transactionId); service re-derives league_id +
  // reversal_date from the transaction row. Used when reverseTransaction's
  // first pass had partial rebuild failures (failed_teams non-empty).
  rebuildTransactionStats: async (leagueId, transactionId) => {
    try {
      const response = await api.post(
        `/api/leagues/${leagueId}/transactions/${transactionId}/rebuild-stats`
      );
      return response.data;
    } catch (error) {
      console.error('Error retrying transaction rebuild:', error);
      throw error;
    }
  },

  getRecentActivity: async (leagueId, hoursBack = 48) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/recent-activity`, {
        params: {
          hours_back: hoursBack,
          limit: 20
        }
      });

      if (response.data.success && response.data.activities) {
        return { success: true, activities: response.data.activities };
      }
      return { success: true, activities: [] };
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      return { success: false, activities: [] };
    }
  },

  // Trades
  createTrade: async (leagueId, tradeData) => {
    const response = await api.post(`/api/leagues/${leagueId}/trades`, tradeData);
    return response.data;
  },

  getTrades: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/trades`);
    return response.data;
  },

  initiateTrade: async (leagueId, tradeData) => {
    const response = await api.post(`/api/leagues/${leagueId}/trades/initiate`, tradeData);
    return response.data;
  },

  // NEW: Trade Management APIs
  proposeTrade: async (leagueId, tradeData) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/trades/propose`, tradeData);
      return response.data;
    } catch (error) {
      console.error('Error proposing trade:', error);
      throw error;
    }
  },

  analyzeTrade: async (leagueId, tradeData) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/trades/analyze`, tradeData);
      return response.data;
    } catch (error) {
      console.error('Error analyzing trade:', error);
      throw error;
    }
  },

  getMyTradeProposals: async (leagueId, targetTeamId = null) => {
    try {
      const params = targetTeamId ? { params: { target_team_id: targetTeamId } } : {};
      const response = await api.get(`/api/leagues/${leagueId}/trades/my-proposals`, params);
      return response.data;
    } catch (error) {
      console.error('Error fetching trade proposals:', error);
      throw error;
    }
  },

  // DEFECT 2 Phase 1 — receiver_placements optional 5th arg (array of
  // placement objects). When action='accept' and provided, receiver's
  // incoming players are placed per the user's per-player designation.
  // When null, backend's Phase 0 auto-assign covers the receiver side.
  respondToTrade: async (leagueId, tradeId, action, rejectionReason = null, commissionerAction = false, receiverPlacements = null) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/trades/${tradeId}/respond`, {
        action: action,
        rejection_reason: rejectionReason,
        commissioner_action: commissionerAction,
        receiver_placements: receiverPlacements,
      });
      return response.data;
    } catch (error) {
      console.error('Error responding to trade:', error);
      throw error;
    }
  },

  cancelTrade: async (leagueId, tradeId) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/trades/${tradeId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling trade:', error);
      throw error;
    }
  },

  commissionerReviewTrade: async (leagueId, tradeId, action, notes = null) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/trades/${tradeId}/review`, {
        action: action,
        commissioner_notes: notes
      });
      return response.data;
    } catch (error) {
      console.error('Error reviewing trade:', error);
      throw error;
    }
  },

  commissionerExecuteTrade: async (leagueId, tradeData) => {
    const response = await api.post(`/api/leagues/${leagueId}/trades/commissioner-execute`, tradeData);
    return response.data;
  },

  getTradeHistory: async (leagueId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.team_id) params.append('team_id', filters.team_id);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);
      
      const queryString = params.toString();
      const url = queryString 
        ? `/api/leagues/${leagueId}/trades?${queryString}` 
        : `/api/leagues/${leagueId}/trades`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching trade history:', error);
      throw error;
    }
  },

  // Consolidated draft-pick endpoint (Bible §4.5.24). tradeableOnly=false (default)
  // returns the full unfiltered draft-room view; tradeableOnly=true applies the
  // phase-dependent tradeability predicate (window + finalizing freeze + not
  // consumed + owned) server-side.
  getTeamDraftPicks: async (leagueId, teamId, tradeableOnly = false) => {
    try {
      const query = tradeableOnly ? '?tradeable_only=true' : '';
      const response = await api.get(`/api/leagues/${leagueId}/teams/${teamId}/draft-picks${query}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching team draft picks:', error);
      throw error;
    }
  },

  getDraftPicksMatrix: async (leagueId, year = null) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/api/leagues/${leagueId}/draft-picks/matrix${query}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching draft picks matrix:', error);
      throw error;
    }
  },

  // Cash & FAAB Management - Phase 7
  getTeamCashBalance: async (leagueId, teamId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/teams/${teamId}/cash-balance`);
      return response.data;
    } catch (error) {
      console.error('Error fetching team cash balance:', error);
      throw error;
    }
  },

  getTeamTransactions: async (leagueId, teamId, limit = 50) => {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit);
      
      const queryString = params.toString();
      const url = queryString 
        ? `/api/leagues/${leagueId}/teams/${teamId}/transactions?${queryString}` 
        : `/api/leagues/${leagueId}/teams/${teamId}/transactions`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching team transactions:', error);
      throw error;
    }
  },

  getTeamCashLedger: async (leagueId, teamId, limit = 50) => {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit);
      
      const queryString = params.toString();
      const url = queryString 
        ? `/api/leagues/${leagueId}/teams/${teamId}/cash-ledger?${queryString}` 
        : `/api/leagues/${leagueId}/teams/${teamId}/cash-ledger`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching team cash ledger:', error);
      throw error;
    }
  },

  adjustTeamCash: async (leagueId, teamId, adjustmentData) => {
    try {
      const response = await api.post(`/api/leagues/${leagueId}/teams/${teamId}/adjust-cash`, adjustmentData);
      return response.data;
    } catch (error) {
      console.error('Error adjusting team cash:', error);
      throw error;
    }
  },

  getAllTeamBalances: async (leagueId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/all-team-balances`);
      return response.data;
    } catch (error) {
      console.error('Error fetching all team balances:', error);
      throw error;
    }
  },

  getCashSettings: async (leagueId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/cash-settings`);
      return response.data;
    } catch (error) {
      console.error('Error fetching cash settings:', error);
      throw error;
    }
  },

  // CONTRACT MANAGEMENT - Commissioner Override
  getLeagueTeamsForContracts: async (leagueId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/salaries/contracts/teams`);
      return response.data;
    } catch (error) {
      console.error('Error fetching teams for contracts:', error);
      throw error;
    }
  },

  getTeamContracts: async (leagueId, teamId) => {
    try {
      const response = await api.get(`/api/leagues/${leagueId}/salaries/contracts/team/${teamId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching team contracts:', error);
      throw error;
    }
  },

  commissionerOverrideContracts: async (leagueId, contracts) => {
    try {
      const response = await api.put(`/api/leagues/${leagueId}/salaries/contracts/commissioner-override`, {
        contracts: contracts,
        update_type: 'both'
      });
      return response.data;
    } catch (error) {
      console.error('Error overriding contracts:', error);
      throw error;
    }
  },

  // Invitations
  inviteOwner: async (leagueId, invitationData) => {
    // Transform camelCase to snake_case for backend compatibility
    const backendData = {
      email: invitationData.ownerEmail,
      owner_name: invitationData.ownerName,
      personal_message: invitationData.personalMessage || ''
    };
    const response = await api.post(`/api/leagues/${leagueId}/invite-owner`, backendData);
    return response.data;
  },

  // NEW: Manual team creation and team-specific invitations
  createManualTeam: async (leagueId, teamName) => {
    const response = await api.post(`/api/leagues/${leagueId}/create-manual-team`, {
      team_name: teamName
    });
    return response.data;
  },

  updateTeamInfo: async (leagueId, teamId, teamData) => {
    const response = await api.put(`/api/leagues/${leagueId}/teams/${teamId}/update-info`, teamData);
    return response.data;
  },

  inviteOwnerToTeam: async (leagueId, teamId, invitationData) => {
    // Transform camelCase to snake_case for backend compatibility
    const backendData = {
      email: invitationData.ownerEmail,
      owner_name: invitationData.ownerName,
      personal_message: invitationData.personalMessage || ''
    };
    const response = await api.post(`/api/leagues/${leagueId}/teams/${teamId}/invite-to-team`, backendData);
    return response.data;
  },

  getInvitations: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/invitations`);
    return response.data;
  },

  cancelInvitation: async (leagueId, invitationId) => {
    const response = await api.delete(`/api/leagues/${leagueId}/invitations/${invitationId}`);
    return response.data;
  },

  resendInvitation: async (leagueId, invitationId) => {
    const response = await api.post(`/api/leagues/${leagueId}/invitations/${invitationId}/resend`);
    return response.data;
  },

  verifyInvitation: async (token) => {
    const response = await api.get(`/api/invitation/verify?token=${encodeURIComponent(token)}`);
    return response.data;
  },

  acceptInvitation: async (token) => {
    const response = await api.post('/api/invitation/accept', { token });
    return response.data;
  },

  toggleCommissionerStatus: async (leagueId, teamId, isCommissioner) => {
    const response = await api.put(`/api/leagues/${leagueId}/teams/${teamId}/commissioner-status`, {
      is_commissioner: isCommissioner
    });
    return response.data;
  },

  // Data Sync
  syncPlayers: async (leagueId) => {
    const response = await api.post(`/api/leagues/${leagueId}/sync-players`);
    return response.data;
  },

  syncAllMLBPlayers: async (leagueId) => {
    const response = await api.post(`/api/leagues/${leagueId}/admin/sync-all-players`);
    return response.data;
  },

  syncLeagueData: async (leagueId) => {
    const response = await api.post(`/api/leagues/${leagueId}/sync-data`);
    return response.data;
  },

  notifyOwners: async (leagueId, notification) => {
    const response = await api.post(`/api/leagues/${leagueId}/notify-owners`, notification);
    return response.data;
  },

  // Public Leagues - NEW ENDPOINT
  getPublicLeagues: async () => {
    try {
      const response = await api.get('/api/leagues/public');
      return response.data;
    } catch (error) {
      console.error('Error fetching public leagues:', error);
      return { success: false, leagues: [] };
    }
  },

  // Position Eligibility Management (Commissioner Only)
  getPositionEligibility: async (leagueId, teamId = null) => {
    try {
      const params = new URLSearchParams();
      if (teamId) params.append('team_id', teamId);
      
      const queryString = params.toString();
      const url = queryString
        ? `/api/leagues/${leagueId}/position-eligibility?${queryString}`
        : `/api/leagues/${leagueId}/position-eligibility`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching position eligibility:', error);
      throw error;
    }
  },

  updatePositionEligibility: async (leagueId, update) => {
    try {
      const response = await api.post(
        `/api/leagues/${leagueId}/position-eligibility/update`,
        update
      );
      return response.data;
    } catch (error) {
      console.error('Error updating position eligibility:', error);
      throw error;
    }
  },

  resetPositionEligibility: async (leagueId, leaguePlayerId) => {
    try {
      const response = await api.delete(
        `/api/leagues/${leagueId}/position-eligibility/${leaguePlayerId}/reset`
      );
      return response.data;
    } catch (error) {
      console.error('Error resetting position eligibility:', error);
      throw error;
    }
  },

  // Team Home Dashboard - Uses existing endpoints from Phase 6
  getTeamHomeData: async (leagueId) => {
    try {
      console.log('🏠 Fetching team home dashboard data...');
      
      // Get user's team first to get team_id
      const userTeamResponse = await leaguesAPI.getUserTeam(leagueId);
      const teamId = userTeamResponse?.team_id;
      
      if (!teamId) {
        throw new Error('Team not found');
      }
      
      // Get most recent box score date (don't assume yesterday had games)
      const availableDatesResponse = await api.get(`/api/leagues/${leagueId}/teams/${teamId}/available-dates`);
      const mostRecentDate = availableDatesResponse.data?.dates?.[0];
      
      if (!mostRecentDate) {
        console.log('⚠️ No box score dates available');
      }
      
      // Make parallel API calls to existing endpoints
      const [
        teamHomeResponse,
        rosterResponse,
        boxScoreResponse,
        probablePitchersResponse,
        standingsResponse,
        playerNotesResponse
      ] = await Promise.allSettled([
        // 1. Get team info from backend endpoint
        api.get(`/api/leagues/${leagueId}/players/team-home-data`),

        // 2. Get roster with stats (canonical)
        leaguesAPI.getMyRosterCanonical(leagueId),

        // 3. Get most recent box score (only if date available)
        mostRecentDate
          ? api.get(`/api/leagues/${leagueId}/teams/${teamId}/box-score?date=${mostRecentDate}`)
          : Promise.resolve({ data: { box_score: { hitting: [], pitching: [] } } }),

        // 4. Get probable pitchers for next 5 days (Phase 6C endpoint)
        // Use ET date, not UTC — after 8 PM ET, UTC date is tomorrow
        api.get(`/api/leagues/${leagueId}/teams/${teamId}/probable-pitchers?start_date=${new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })}&days=5`),

        // 5. Get standings for rank + total points
        leaguesAPI.getLeagueStandings(leagueId),

        // 6. Get Rotowire player news (full set, cached 30 min — filter client-side by roster)
        mlbAPI.getPlayerNotes()
      ]);
      
      // Extract team info
      const teamInfo = teamHomeResponse.status === 'fulfilled' 
        ? teamHomeResponse.value?.data?.team || {}
        : {};
      
      // Extract standings and calculate rank + total points
      let rank = null;
      let totalPoints = null;
      let totalTeams = null;
      
      if (standingsResponse.status === 'fulfilled' && standingsResponse.value?.standings) {
        const standings = standingsResponse.value.standings;
        totalTeams = standings.length;
        
        // Find user's team in standings
        const userTeamStanding = standings.find(team => team.team_id === teamId);
        
        if (userTeamStanding) {
          totalPoints = userTeamStanding.total_points || userTeamStanding.totalPoints || 0;
          
          // Calculate rank (sort by totalPoints descending)
          const sortedStandings = [...standings].sort(
            (a, b) => (b.total_points || b.totalPoints || 0) - (a.total_points || a.totalPoints || 0)
          );
          rank = sortedStandings.findIndex(team => team.team_id === teamId) + 1;
        }
      }
      
      // Add standings data to team_info
      teamInfo.rank = rank;
      teamInfo.total_points = totalPoints;
      teamInfo.total_teams = totalTeams;
      
      // Extract roster stats (convert canonical format to dashboard format)
      const rosterPlayers = rosterResponse.status === 'fulfilled'
        ? rosterResponse.value?.players || []
        : [];
      
      const rosterStats = rosterPlayers;
      
      // Extract box scores
      const boxScoreData = boxScoreResponse.status === 'fulfilled'
        ? boxScoreResponse.value?.data?.box_score || {}
        : {};
      
      const lastNightBox = {
        hitting: boxScoreData.hitting || [],
        pitching: boxScoreData.pitching || []
      };
      
      // Extract probable pitchers and convert to starting_pitchers format
      const probablePitchersData = probablePitchersResponse.status === 'fulfilled'
        ? probablePitchersResponse.value?.data || {}
        : {};
      
      console.log('PROBABLE RAW:', JSON.stringify(probablePitchersData, null, 2));
      const startingPitchers = probablePitchersData.games || [];
      console.log('GAMES RAW:', JSON.stringify(startingPitchers[0], null, 2));

      console.log('✅ Team home data fetched successfully:', {
        team: teamInfo.team_name,
        roster_count: rosterStats.length,
        box_hitting: lastNightBox.hitting.length,
        box_pitching: lastNightBox.pitching.length,
        starting_pitchers: startingPitchers.length
      });
      
      return {
        success: true,
        team_info: teamInfo,
        roster_stats: rosterStats,
        starting_pitchers: startingPitchers,
        player_notes: playerNotesResponse.status === 'fulfilled' && Array.isArray(playerNotesResponse.value)
          ? playerNotesResponse.value
          : [],
        last_night_box: lastNightBox
      };
      
    } catch (error) {
      console.error('❌ Error fetching team home data:', error);
      // Return empty structure on error so dashboard still loads
      return {
        success: false,
        team_info: {},
        roster_stats: [],
        starting_pitching: [],
        player_notes: [],
        last_night_box: { hitting: [], pitching: [] },
        error: error.message
      };
    }
  },

  // =============================================================================
  // MESSAGE BOARD API
  // =============================================================================

  // Get messages for a league
  getMessages: async (leagueId, limit = 50, offset = 0, includeReplies = true) => {
    const response = await api.get(`/api/leagues/${leagueId}/messages`, {
      params: { limit, offset, include_replies: includeReplies }
    });
    return response.data;
  },

  // Get a single message by ID
  getMessageById: async (leagueId, messageId) => {
    const response = await api.get(`/api/leagues/${leagueId}/messages/${messageId}`);
    return response.data;
  },

  // Post a new message or reply
  postMessage: async (leagueId, messageData) => {
    const response = await api.post(`/api/leagues/${leagueId}/messages`, messageData);
    return response.data;
  },

  // Update a message
  updateMessage: async (leagueId, messageId, messageData) => {
    const response = await api.put(`/api/leagues/${leagueId}/messages/${messageId}`, messageData);
    return response.data;
  },

  // Delete a message
  deleteMessage: async (leagueId, messageId) => {
    const response = await api.delete(`/api/leagues/${leagueId}/messages/${messageId}`);
    return response.data;
  },

  // Toggle pin on a message (commissioner only)
  togglePinMessage: async (leagueId, messageId, isPinned) => {
    const response = await api.post(`/api/leagues/${leagueId}/messages/${messageId}/pin`, {
      is_pinned: isPinned
    });
    return response.data;
  },

  // Get unread message count
  getUnreadCount: async (leagueId, sinceTimestamp = null) => {
    const params = sinceTimestamp ? { since_timestamp: sinceTimestamp } : {};
    const response = await api.get(`/api/leagues/${leagueId}/messages/unread/count`, { params });
    return response.data;
  },

  // Get message statistics
  getMessageStats: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/messages/stats`);
    return response.data;
  },

  // Get presigned URL for image upload
  getMessageImageUploadUrl: async (leagueId, filename, contentType) => {
    const response = await api.post(`/api/leagues/${leagueId}/messages/upload-image`, {
      filename: filename,
      content_type: contentType
    });
    return response.data;
  },

  // Upload image to S3 using presigned URL
  uploadImageToS3: async (presignedUrl, file) => {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
    
    return { success: true, status: response.status };
  },

  // Complete image upload workflow
  uploadMessageImage: async (leagueId, file) => {
    try {
      console.log('📸 Starting message image upload for:', file.name);
      
      // Get presigned URL
      const urlResponse = await leaguesAPI.getMessageImageUploadUrl(leagueId, file.name, file.type);
      
      if (!urlResponse.success) {
        throw new Error(urlResponse.message || 'Failed to get upload URL');
      }
      
      // Upload to S3
      await leaguesAPI.uploadImageToS3(urlResponse.upload_url, file);
      
      console.log('✅ Image uploaded successfully to:', urlResponse.public_url);
      
      return {
        success: true,
        image_url: urlResponse.public_url,
        s3_key: urlResponse.s3_key
      };
      
    } catch (error) {
      console.error('❌ Image upload failed:', error);
      throw error;
    }
  },

  getDashboardLayout: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/preferences/dashboard-layout`);
    return response.data;
  },

  saveDashboardLayout: async (leagueId, layout) => {
    const response = await api.put(`/api/leagues/${leagueId}/preferences/dashboard-layout`, { layout });
    return response.data;
  },

  getMLBTransactions: async (daysBack = 3) => {
    const response = await api.get(`/api/mlb/transactions?days_back=${daysBack}`);
    return response.data;
  },

  getFreeAgentNews: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/mlb/free-agent-news`);
    return response.data;
  },

  getTradingBlock: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/trading-block`);
    return response.data;
  },

  addToTradingBlock: async (leagueId, payload) => {
    const response = await api.post(`/api/leagues/${leagueId}/trading-block`, payload);
    return response.data;
  },

  removeFromTradingBlock: async (leagueId, blockId) => {
    const response = await api.delete(`/api/leagues/${leagueId}/trading-block/${blockId}`);
    return response.data;
  },

  // Live Scoring
  getLiveScoringTeamStats: async (leagueId, teamId = null) => {
    const params = teamId ? { params: { team_id: teamId } } : {};
    const response = await api.get(`/api/leagues/${leagueId}/live-scoring/team-stats`, params);
    return response.data;
  },

  getLiveScoringStandings: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/live-scoring/standings`);
    return response.data;
  },

  getLiveScoringGames: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/live-scoring/games`);
    return response.data;
  },

  getGameDetail: async (leagueId, gamePk) => {
    const response = await api.get(`/api/leagues/${leagueId}/live-scoring/games/${gamePk}`);
    return response.data;
  },

  getAllTeamsLiveStats: async (leagueId) => {
    const response = await api.get(`/api/leagues/${leagueId}/live-scoring/all-teams`);
    return response.data;
  },

  // =========================================================================
  // OPTIMIZER ENDPOINTS
  // =========================================================================

  // DD-RO-ASYNC-0728 Phase 3: submit a heavy optimizer run. Returns in <1s with either a finished result
  // (cache hit, or the backend sync-fallback when the async kill-switch is flipped) or {job_id, status:
  // 'running'} to poll. kind = 'full' | 'trades'.
  submitOptimizer: async (leagueId, teamId, mode = 'balanced', kind = 'full') => {
    const response = await api.post(`/api/leagues/${leagueId}/optimizer/${kind}/submit?team_id=${teamId}&mode=${mode}`);
    return response.data;
  },

  getOptimizerJob: async (leagueId, jobId) => {
    const response = await api.get(`/api/leagues/${leagueId}/optimizer/job/${jobId}`);
    return response.data;
  },

  // Submit → poll → result. The heavy run executes in a worker, so each poll is a fast GET and this is NOT
  // bounded by the 29s API-Gateway ceiling. Gives up after MAX_WAIT with an honest error (NEVER polls
  // forever). onProgress('computing') lets a caller show a computing state; the backend async flag is
  // transparent here (a cache hit / sync fallback simply returns inline on submit).
  runOptimizer: async (leagueId, teamId, mode = 'balanced', kind = 'full', opts = {}) => {
    const { onProgress } = opts;
    const MAX_WAIT_MS = 150000;                 // ~2.5 min ceiling on the client wait, then a clean error
    const first = await leaguesAPI.submitOptimizer(leagueId, teamId, mode, kind);
    if (first && first.status === 'done' && first.result) return first.result;
    if (first && first.status === 'error') throw new Error(first.error || 'Optimization failed');
    const jobId = first && first.job_id;
    if (!jobId) throw new Error('Optimizer did not return a job id');
    if (onProgress) onProgress('computing');
    const start = Date.now();
    let delay = 1500;
    while (Date.now() - start < MAX_WAIT_MS) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, delay));
      delay = Math.min(delay + 250, 3000);      // gentle backoff, capped
      let job;
      try {
        // eslint-disable-next-line no-await-in-loop
        job = await leaguesAPI.getOptimizerJob(leagueId, jobId);
      } catch (e) {
        if (e?.response?.status === 404) throw new Error('Optimization job expired — please retry.');
        continue;                               // transient — keep polling within the max-wait
      }
      if (job.status === 'done') return job.result;
      if (job.status === 'error') throw new Error(job.error || 'Optimization failed');
    }
    throw new Error('Optimization is taking longer than expected. Please try again.');
  },

  // DD-RO-ASYNC-0728: now runs through the async submit→poll path (signature unchanged, so the existing
  // call sites — OptimizerDashboard.loadData / MobileOptimizer.fetchTrades — use async transparently).
  getOptimizerFull: async (leagueId, teamId, mode = 'balanced', opts = {}) =>
    leaguesAPI.runOptimizer(leagueId, teamId, mode, 'full', opts),

  getOptimizerTrades: async (leagueId, teamId, mode = 'balanced', opts = {}) =>
    leaguesAPI.runOptimizer(leagueId, teamId, mode, 'trades', opts),

  getOptimizerGaps: async (leagueId, teamId) => {
    const response = await api.get(`/api/leagues/${leagueId}/optimizer/gaps?team_id=${teamId}`);
    return response.data;
  },

  getOptimizerAlternatives: async (leagueId, playerId, teamId, mode = 'balanced') => {
    const response = await api.get(`/api/leagues/${leagueId}/optimizer/alternatives/${playerId}?team_id=${teamId}&mode=${mode}`);
    return response.data;
  },

  getPersonalizedValue: async (leagueId, playerId, teamId, mode = 'balanced') => {
    const response = await api.get(`/api/leagues/${leagueId}/optimizer/personalized-value/${playerId}?team_id=${teamId}&mode=${mode}`);
    return response.data;
  },

  getOptimizerSequences: async (leagueId, teamId, maxMoves = 5, mode = 'balanced') => {
    const response = await api.get(`/api/leagues/${leagueId}/optimizer/sequences?team_id=${teamId}&max_moves=${maxMoves}&mode=${mode}`);
    return response.data;
  },

  getTargetedTrades: async (leagueId, teamId, playerId, direction, mode = 'balanced', pickRound = null, pickYear = null) => {
    const params = { team_id: teamId, direction, mode };
    if (playerId) params.player_id = playerId;
    if (pickRound) params.pick_round = pickRound;
    if (pickYear) params.pick_year = pickYear;
    const response = await api.get(
      `/api/leagues/${leagueId}/optimizer/targeted-trades`,
      { params }
    );
    return response.data;
  },
};

// =============================================================================
// PLAYERS API (Global - No League Context)
// =============================================================================

export const playersAPI = {
  getPlayers: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.position) params.append('position', filters.position);
    if (filters.team) params.append('team', filters.team);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const queryString = params.toString();
    const url = queryString ? `/api/players?${queryString}` : '/api/players';

    const response = await api.get(url);
    return response.data;
  },

  getPlayerById: async (playerId) => {
    const response = await api.get(`/api/players/${playerId}`);
    return response.data;
  },

  // Global player search (no league context) - CANONICAL
  searchPlayers: async (searchTerm, limit = 12, options = {}) => {
    try {
      if (!searchTerm || searchTerm.trim().length < 2) {
        return { success: false, players: [], message: 'Search term too short' };
      }

      console.log('🔍 Calling canonical /search with:', {
        q: searchTerm.trim(),
        limit: limit
      });

      // DD-FASEARCH-RACE-0730: optional AbortSignal so a superseded in-flight search
      // can be cancelled (backward compatible — omit to keep prior behavior).
      const response = await api.get('/api/players/search', {
        params: {
          q: searchTerm.trim(),
          limit: limit
        },
        ...(options.signal ? { signal: options.signal } : {})
      });
      
      // Response format: { success: true, players: [...], count: 10, query: "..." }
      // Each player has: { ids: { mlb: 12345 }, info: {...}, stats: {...} }
      return response.data;
    } catch (error) {
      console.error('❌ Player search error:', error.response);
      throw error;
    }
  },

  // Get player across all user's leagues (multi-league view) - NEW
  getPlayerAcrossMyLeagues: async (playerId) => {
    try {
      const response = await api.get(`/api/players/players/${playerId}/my-leagues`);
      // Response: { ids: {...}, info: {...}, stats: {...}, league_contexts: [...], summary: {...} }
      return response.data;
    } catch (error) {
      console.error('❌ Multi-league player fetch error:', error);
      throw error;
    }
  },

  // getPlayerInfoCard — KILLED in Phase 4: analytics absorbed into /modal endpoint

  // Player Overview Tab Data - PHASE 3 (Targeted 10-15KB endpoint)
  getPlayerOverview: async (playerId, leagueId = null, season = null) => {
    try {
      const params = new URLSearchParams();
      if (season) params.append('season', season);
      
      const url = leagueId 
        ? `/api/players/leagues/${leagueId}/players/${playerId}/overview?${params}`
        : `/api/players/${playerId}/overview?${params}`;
      
      console.log(`📊 Fetching Overview data: ${url}`);
      
      const response = await api.get(url);
      console.log('✅ Overview data received (~10-15KB):', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch Overview data:', error);
      throw error;
    }
  },

  // Player Game Logs Full - PHASE 4 (Combined logs + analytics ~20KB endpoint)
  getPlayerGameLogsFull: async (playerId, leagueId = null, season = null, limit = 20) => {
    try {
      const params = new URLSearchParams();
      if (season) params.append('season', season);
      if (limit) params.append('limit', limit);
      
      const url = leagueId 
        ? `/api/players/leagues/${leagueId}/players/${playerId}/game-logs-full?${params}`
        : `/api/players/${playerId}/game-logs-full?${params}`;
      
      console.log(`📊 Fetching Game Logs Full data: ${url}`);
      
      const response = await api.get(url);
      console.log('✅ Game Logs Full data received (~20KB):', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch Game Logs Full data:', error);
      throw error;
    }
  },

  // Player Historical Tab Data - PHASE 6 (Career progression ~15KB endpoint)
  getPlayerHistorical: async (playerId, leagueId = null, season = null) => {
    try {
      const params = new URLSearchParams();
      if (season) params.append('season', season);
      
      const url = leagueId 
        ? `/api/players/leagues/${leagueId}/players/${playerId}/historical?${params}`
        : `/api/players/${playerId}/historical?${params}`;
      
      console.log(`📊 Fetching Historical data: ${url}`);
      
      const response = await api.get(url);
      console.log('✅ Historical data received (~15KB):', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch Historical data:', error);
      throw error;
    }
  },

  // Player Advanced Tab Data - PHASE 7 (Splits/Projections/Fantasy/Comparison ~40KB endpoint)
  getPlayerAdvanced: async (playerId, leagueId = null) => {
    try {
      const url = leagueId
        ? `/api/players/leagues/${leagueId}/players/${playerId}/advanced`
        : `/api/players/${playerId}/advanced`;
      
      console.log(`📊 Fetching Advanced data: ${url}`);
      
      const response = await api.get(url);
      console.log('✅ Advanced data received (~40KB):', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch Advanced data:', error);
      throw error;
    }
  },

  // getCareerStats — KILLED in Phase 4: reads from /modal player.career_stats
  // getPlayerCareer — KILLED in Phase 4: reads from /modal player.career_stats

  // ── v3 projection-insight surfaces (mirror desktop useProjectionInsights) ──
  // Status-safe: the services return {status:'ok'|'no_covariates'|'no_gap_vector'
  // |'empty_cohort', ...}. These return the payload only when status==='ok',
  // else null → the UI block shows its honest-empty fallback (no fabrication).
  // IL Risk = all players; gap-anomaly / comparables = hitters, archive targets.
  getPlayerIlRisk: async (playerId) => {
    const r = await api.get(`/api/analytics/players/${playerId}/il-risk`);
    return r?.data?.status === 'ok' ? r.data : null;
  },
  getPlayerGapAnomaly: async (playerId, targetStat) => {
    const r = await api.get(`/api/analytics/players/${playerId}/gap-anomaly/${targetStat}`);
    return r?.data?.status === 'ok' ? r.data : null;
  },
  // Gap-pattern comparables — comparables overhaul Gate 2b: NO LONGER the consumer
  // comps card (see getPlayerProfileComps). Kept for a future labeled power view.
  getPlayerComparables: async (playerId, targetStat, k = 5) => {
    const r = await api.get(`/api/analytics/players/${playerId}/comparables/${targetStat}?k=${k}`);
    return r?.data?.status === 'ok' ? r.data : null;
  },
  // Profile Comparables (overhaul Gate 2a) — the consumer comps card. Physical-profile
  // kNN (stabilized contact profile: EV, barrel%, hard-hit%, launch, xwOBA) with a
  // survivorship-honest base rate. Returns the payload INCLUDING covered:false shapes
  // ({covered:false, reason:'pitcher'|'insufficient_sample'|'not_current'}) so the UI
  // renders the labeled absent states (Gate 3); null only on a malformed response.
  getPlayerProfileComps: async (playerId) => {
    const r = await api.get(`/api/analytics/players/${playerId}/profile-comps`);
    return r?.data && typeof r.data.covered === 'boolean' ? r.data : null;
  },
  // Projection-history series (the v3 daily projection sparkline). Same endpoint
  // desktop's useProjectionInsights uses; exposed as a shared client method so
  // mobile calls it without touching desktop code. Returns {values, count,
  // window_days, ...} or null on empty/non-ok.
  getPlayerProjectionSeries: async (playerId, targetStat, playerType = 'hitter') => {
    const r = await api.get(`/api/analytics/players/${playerId}/projection-series/${targetStat}?player_type=${playerType}`);
    return r?.data?.status === 'ok' ? r.data : null;
  },

  // Fantasy Impact endpoints (Phase 1-3: player impact).
  // NOTE: getTeamCategoryStandings moved to leaguesAPI (DD-RO-STANDINGS-FN-CRASH-0728) — its endpoint is a
  // /api/leagues/... route and every optimizer consumer calls it as leaguesAPI.getTeamCategoryStandings.
  getPlayerFantasyImpact: async (leagueId, teamId, playerId) => {
    const response = await api.get(`/api/leagues/${leagueId}/teams/${teamId}/players/${playerId}/fantasy-impact`);
    return response.data;
  },

  getRecentPerformance: async (playerId, days = 7) => {
    const response = await api.get(`/api/players/${playerId}/recent-performance`, {
      params: { days }
    });
    return response.data;
  },

  // LEGACY: Game logs only (no analytics) - use getPlayerGameLogsFull instead
  getGameLogs: async (playerId, options = {}) => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.days) params.append('days', options.days);
    
    const queryString = params.toString();
    const url = queryString 
      ? `/api/players/${playerId}/game-logs?${queryString}`
      : `/api/players/${playerId}/game-logs`;
    
    const response = await api.get(url);
    return response.data;
  },

  getAnalytics: async (playerId) => {
    const response = await api.get(`/api/players/${playerId}/analytics`);
    return response.data;
  },

  // UPDATED: Tile Analytics endpoints now accept optional leagueId
  getPitcherTileAnalytics: async (playerId, leagueId = null) => {
    try {
      // NEW: League context in URL path, not query param
      const url = leagueId 
        ? `/api/analytics/leagues/${leagueId}/players/${playerId}/pitcher-tiles`
        : `/api/analytics/players/${playerId}/pitcher-tiles`;
      
      console.log(`📊 Fetching pitcher tile analytics: ${url}`);
      
      const response = await api.get(url);
      console.log('✅ Pitcher tile analytics received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch pitcher tile analytics:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  getHitterTileAnalytics: async (playerId, leagueId = null) => {
    try {
      // NEW: League context in URL path, not query param
      const url = leagueId 
        ? `/api/analytics/leagues/${leagueId}/players/${playerId}/hitter-tiles`
        : `/api/analytics/players/${playerId}/hitter-tiles`;
      
      console.log(`📊 Fetching hitter tile analytics: ${url}`);
      
      const response = await api.get(url);
      console.log('✅ Hitter tile analytics received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch hitter tile analytics:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  getPlayerSplits: async (playerId, leagueId = null, season = null) => {
    try {
      // League context in URL path
      const url = leagueId 
        ? `/api/analytics/leagues/${leagueId}/players/${playerId}/splits`
        : `/api/analytics/players/${playerId}/splits`;
      
      const params = season ? { season } : {};
      
      console.log(`📊 Fetching player splits: ${url}`);
      
      const response = await api.get(url, { params });
      console.log('✅ Player splits received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch player splits:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  // Comprehensive Analytics (Splits + Projections + Fantasy)
  getPlayerComprehensiveAnalytics: async (playerId, leagueId = null, options = {}) => {
    try {
      const { 
        season = null, 
        include_projections = true, 
        include_fantasy = true 
      } = options;
      
      const url = leagueId 
        ? `/api/analytics/leagues/${leagueId}/players/${playerId}/comprehensive`
        : `/api/analytics/players/${playerId}/comprehensive`;
      
      const params = {};
      if (season) params.season = season;
      if (include_projections !== undefined) params.include_projections = include_projections;
      if (include_fantasy !== undefined) params.include_fantasy = include_fantasy;
      
      console.log(`📊 Fetching comprehensive analytics: ${url}`);
      
      const response = await api.get(url, { params });
      console.log('✅ Comprehensive analytics received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch comprehensive analytics:', error);
      throw error;
    }
  },

  // Rankings API Methods (Phase 4D)
  getPlayerRankingsMLB: async (playerId, season = null) => {
    try {
      const url = `/api/analytics/players/${playerId}/rankings/mlb`;
      const params = season ? { season } : {};
      
      console.log(`📊 Fetching MLB rankings: ${url}`);
      
      const response = await api.get(url, { params });
      console.log('✅ MLB rankings received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch MLB rankings:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  getPlayerRankingsPosition: async (playerId, season = null) => {
    try {
      const url = `/api/analytics/players/${playerId}/rankings/position`;
      const params = season ? { season } : {};
      
      console.log(`📊 Fetching position rankings: ${url}`);
      
      const response = await api.get(url, { params });
      console.log('✅ Position rankings received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch position rankings:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  getPlayerRankingsLeague: async (playerId, leagueId, season = null) => {
    try {
      const url = `/api/analytics/leagues/${leagueId}/players/${playerId}/rankings`;
      const params = season ? { season } : {};
      
      console.log(`📊 Fetching league rankings: ${url}`);
      
      const response = await api.get(url, { params });
      console.log('✅ League rankings received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch league rankings:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },

  getLeagueAverages: async (leagueId, season = null) => {
    try {
      const url = `/api/analytics/leagues/${leagueId}/averages`;
      const params = season ? { season } : {};
      
      console.log(`📊 Fetching league averages: ${url}`);
      
      const response = await api.get(url, { params });
      console.log('✅ League averages received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch league averages:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },
};

// =============================================================================
// UTILITIES API
// =============================================================================

export const utilitiesAPI = {
  healthCheck: async () => {
    const response = await api.get('/api/health');
    return response.data;
  }
};

// =============================================================================
// HELPER FUNCTIONS FOR ASYNC OPERATIONS
// =============================================================================

export const createLeagueWithPolling = async (leagueData, callbacks = {}) => {
  const { onProgress, onComplete, onError, pollInterval = 3000, maxAttempts = 60 } = callbacks;

  try {
    const createResponse = await leaguesAPI.createLeague(leagueData);

    if (!createResponse.success || !createResponse.league_id) {
      throw new Error(createResponse.message || 'Failed to start league creation');
    }

    const leagueId = createResponse.league_id;
    let attempts = 0;

    const pollStatus = async () => {
      try {
        attempts++;
        const statusResponse = await leaguesAPI.checkLeagueCreationStatus(leagueId);

        if (statusResponse.success) {
          if (onProgress) {
            onProgress(statusResponse);
          }

          if (statusResponse.status === 'completed') {
            if (onComplete) {
              onComplete(statusResponse);
            }
            return statusResponse;

          } else if (statusResponse.status === 'failed') {
            const error = new Error(statusResponse.message || 'League creation failed');
            if (onError) {
              onError(error);
            }
            throw error;

          } else if (attempts >= maxAttempts) {
            const error = new Error('League creation timed out');
            if (onError) {
              onError(error);
            }
            throw error;

          } else {
            setTimeout(pollStatus, pollInterval);
          }
        }
      } catch (error) {
        if (onError) {
          onError(error);
        }
        throw error;
      }
    };

    setTimeout(pollStatus, pollInterval);
    return { league_id: leagueId, status: 'polling_started' };

  } catch (error) {
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

// =============================================================================
// CASH & LEDGER API - Phase 7
// =============================================================================

export const cashAPI = {
  getTeamBalance: async (leagueId, teamId) => {
    return await leaguesAPI.getTeamCashBalance(leagueId, teamId);
  },

  getTeamLedger: async (leagueId, teamId, limit = 50) => {
    return await leaguesAPI.getTeamCashLedger(leagueId, teamId, limit);
  },

  manualCashAdjustment: async (leagueId, teamId, amount, description) => {
    return await leaguesAPI.adjustTeamCash(leagueId, teamId, {
      amount: amount,
      description: description,
      transaction_type: 'manual_adjustment'
    });
  },

  getLeagueTeamBalances: async (leagueId) => {
    return await leaguesAPI.getAllTeamBalances(leagueId);
  },

  getCashSettings: async (leagueId) => {
    return await leaguesAPI.getCashSettings(leagueId);
  }
};

// =============================================================================
// WATCHLIST API - 9l.2
// Mobile-internal Watch button + MobileWatchList page call these. Backend
// infrastructure lives at backend/src/routers/watchlist_new.py and is shared
// with the desktop WatchListStar component. Per Tony's mobile architecture rule,
// mobile reuses BACKEND endpoints only — never desktop frontend components.
// =============================================================================

export const watchlistAPI = {
  // Toggle: add a player to the watch list. Backend takes player_id as query param.
  addToWatchList: async (playerId) => {
    const response = await api.post(`/api/watchlist/add?player_id=${playerId}`);
    return response.data;
  },

  // Toggle: remove a player from the watch list.
  removeFromWatchList: async (playerId) => {
    const response = await api.delete(`/api/watchlist/remove/${playerId}`);
    return response.data;
  },

  // Initial-state lookup so a Watch button can render the correct icon on mount.
  getWatchListStatus: async (playerId) => {
    const response = await api.get(`/api/watchlist/player/${playerId}/status`);
    return response.data;
  },

  // Full watch list (used by MobileWatchList page).
  getWatchList: async () => {
    const response = await api.get(`/api/watchlist`);
    return response.data;
  },
};

// =============================================================================
// DEFAULT EXPORT - Support direct api.get(), api.post() calls
// =============================================================================

export default {
  // Direct access to axios methods
  get: api.get.bind(api),
  post: api.post.bind(api),
  put: api.put.bind(api),
  delete: api.delete.bind(api),
  patch: api.patch.bind(api),
  
  // Named API exports for organized access
  auth: authAPI,
  account: accountAPI,
  leagues: leaguesAPI,
  players: playersAPI,
  utilities: utilitiesAPI,
  mlb: mlbAPI,  // MLB API
  cash: cashAPI,  // Cash & Ledger API - Phase 7
  watchlist: watchlistAPI,  // 9l.2 — mobile + desktop watchlist
};

// Build timestamp: MLB Data API Integration Complete - Tile Analytics Updated - Cash API Added